/**
 * Product Questions Component
 *
 * SIMPLIFIED UI-ONLY COMPONENT:
 * - Toggle question form visibility
 * - Smooth Alpine.js transitions
 * - No data operations (handled by server server-side)
 *
 * @returns {Object} Alpine.js component
 */
export default function productQuestions() {
  return {
    // Component State
    showForm: false,

    /**
     * Initialize component
     */
    init() {
      // Check if there's a form error (redirect after submission with errors)
      // In that case, show the form
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('show_question_form') === 'true') {
        this.showForm = true;
      }

      // Listen for successful submission messages
      this.checkForSuccessMessage();
    },

    /**
     * Toggle question form visibility
     */
    toggleForm() {
      this.showForm = !this.showForm;

      // Scroll to form when opening
      if (this.showForm) {
        this.$nextTick(() => {
          const formElement = this.$el.querySelector('form');
          if (formElement) {
            formElement.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
            });

            // Focus first input
            const firstInput = formElement.querySelector('textarea, input[type="text"]');
            if (firstInput) {
              setTimeout(() => firstInput.focus(), 300);
            }
          }
        });
      }
    },

    /**
     * Check for success message after form submission
     */
    checkForSuccessMessage() {
      // server redirects with success notice after submission
      // We can check for notices in the DOM
      const successNotice = document.querySelector('.notices.success');
      if (successNotice) {
        // Close form if it was open
        this.showForm = false;

        // Scroll to notices
        successNotice.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    },

    /**
     * Validate form before submission
     * Returns true if valid, false otherwise
     */
    validateForm(event) {
      const form = event.target;
      const question = form.querySelector('[name="question"]');
      const name = form.querySelector('[name="name"]');
      const email = form.querySelector('[name="email"]');

      let isValid = true;
      let errorMessage = '';

      // Validate question
      if (!question || !question.value.trim()) {
        isValid = false;
        errorMessage = window.tx('errorEnterQuestion', 'Please enter your question.');
      }
      // Validate name
      else if (!name || !name.value.trim()) {
        isValid = false;
        errorMessage = window.tx('errorEnterName', 'Please enter your name.');
      }
      // Validate email
      else if (!email || !email.value.trim()) {
        isValid = false;
        errorMessage = window.tx('errorEnterEmail', 'Please enter your email address.');
      }
      // Validate email format
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())) {
        isValid = false;
        errorMessage = window.tx('errorValidEmail', 'Please enter a valid email address.');
      }

      // Show error if validation fails
      if (!isValid) {
        event.preventDefault();
        this.showError(errorMessage);
        return false;
      }

      return true;
    },

    /**
     * Show error message
     */
    showError(message) {
      // Create temporary error message
      const errorDiv = document.createElement('div');
      errorDiv.className =
        'neu-card p-4 mb-4 bg-red-100 dark:bg-red-900/20 border-2 border-red-500 text-red-800 dark:text-red-200';
      errorDiv.innerHTML = `
        <div class="flex items-center gap-3">
          <svg class="w-5 h-5 flex-shrink-0"><use href="#icon-alert-circle"></use></svg>
          <p class="text-sm font-medium">${message}</p>
        </div>
      `;

      // Insert before form
      const form = this.$el.querySelector('form');
      if (form) {
        form.parentElement.insertBefore(errorDiv, form);

        // Scroll to error
        errorDiv.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });

        // Remove after 5 seconds
        setTimeout(() => {
          errorDiv.remove();
        }, 5000);
      }
    },
  };
}
